import { createContext, useContext, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './AlertProvider.css';

// Create the Alert Context
const AlertContext = createContext();

// Hook to use Alerts
export const useAlert = () => {
    return useContext(AlertContext);
};

// Alert Provider Component
export const AlertProvider = ({ children }) => {
    const [alerts, setAlerts] = useState([]);

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    };

    // Generate a unique ID
    const generateUniqueId = () => `${Date.now()}-${Math.random()}`;

    const showAlert = (() => {
        let lastAlertId = null;
    
        return (type, description) => {
            const id = generateUniqueId();
    
            // Prevent duplicate alerts
            if (lastAlertId === description) return;
    
            lastAlertId = description;
    
            setAlerts((prevAlerts) => [...prevAlerts, { id, type, description }]);
    
            setTimeout(() => {
                setAlerts((prevAlerts) => prevAlerts.filter(alert => alert.id !== id));
                lastAlertId = null; // Reset after timeout
            }, 5000);
        };
    })();

    // Remove an alert manually
    const closeAlert = (id) => {
        setAlerts((prevAlerts) => prevAlerts.filter(alert => alert.id !== id));
    };

    // Remove all alerts when Escape key is pressed
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                setAlerts([]); // Clear all alerts
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => {
            window.removeEventListener('keydown', handleEscapeKey);
        };
    }, []);

    return (
        <AlertContext.Provider value={{ showAlert }}>
            {children}
            <div className="alerts-container">
                <AnimatePresence>
                    {alerts.map((alert) => (
                        <motion.div
                            key={alert.id}
                            className={`alert alert-${alert.type}`}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 50 }}
                            transition={{ duration: 0.3 }}
                        >
                            <button
                                className="alert-close-btn"
                                onClick={() => closeAlert(alert.id)}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                            <div className="alert-content">
                                <span>{capitalizeFirstLetter(alert.type)}</span>
                                <p>{alert.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </AlertContext.Provider>
    );
};